import { createPath } from 'history';
import React, { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';

import { AnyAction } from 'redux';
import ClipSelectPage, {
  ClipSelectPageProps,
  OnOpenInAdvancedEditor,
  OnReprocessClip,
  OnSubmit,
} from 'blocks/ClipSelect';
import { ClipSuggestion } from 'blocks/ClipSelect/types';
import {
  enableCreationClipSuggestion,
  getCreationClipSuggestion,
  getCreationClipSuggestions,
} from 'redux/middleware/api/creation-service/actions';
import {
  createCreationClipSuggestion,
  dislikeCreationSuggestedClip,
  reprocessCreationSuggestion,
} from 'redux/modules/clip-select';
import {
  onClickDownloadAllClips,
  onClickExportAllClips,
} from 'redux/modules/mixpanel';
import { showError } from 'redux/modules/notification';
import { Dispatch, State } from 'redux/types';
import { ClipSuggestionDislikeReason } from 'types';
import { getDownloadPath, getProjectPath } from 'utils/routes';
import { suggestedClipsSelector, suggestionSelector } from './selectors';
import useVideoTranscriptionSuggestedClipInfo from './useVideoTranscriptionSuggestedClipInfo';

export interface VideoTranscriptionSuggestedClipsPageProps {}

type HandleSelectClip = ClipSelectPageProps['onSelectClip'];
type HandleDislikeClip = ClipSelectPageProps['onDislikeClip'];

const VideoTranscriptionSuggestedClipsPage: React.FC<VideoTranscriptionSuggestedClipsPageProps> = () => {
  const store = useStore<State, AnyAction>();
  const dispatch = useDispatch<Dispatch>();

  const {
    creationRequestId,
    eddyProjectId,
    suggestedClips,
    dislikedSuggestionIds,
    clipsPageInfo,
    source,
  } = useVideoTranscriptionSuggestedClipInfo();

  const handleSubmit: OnSubmit = async clipSuggestion => {
    try {
      await dispatch(
        enableCreationClipSuggestion(creationRequestId, clipSuggestion.id),
      );

      return getDownloadPath(clipSuggestion.widgetId);
    } catch {
      dispatch(showError('Error sharing clip', 5));
      return undefined;
    }
  };

  const handleDislikeClip: HandleDislikeClip = useCallback(
    async (
      reason: ClipSuggestionDislikeReason,
      clipSuggestion: ClipSuggestion,
      onClipDisliked?: () => void,
    ): Promise<void> => {
      try {
        await dispatch(
          dislikeCreationSuggestedClip(
            reason,
            clipSuggestion.id,
            clipSuggestion.id,
          ),
        );
        onClipDisliked?.();
      } catch {
        dispatch(showError({ message: 'Failed to remove clip.' }));
      }
    },
    [dispatch],
  );

  const handleSelectClip: HandleSelectClip = useCallback(
    async ({ region, activeSuggestionId }): Promise<number | void> => {
      const suggestionId = await dispatch(
        createCreationClipSuggestion(
          creationRequestId,
          activeSuggestionId,
          region.startMillis,
          region.endMillis,
        ),
      );

      return suggestionId;
    },
    [creationRequestId, dispatch],
  );

  const handleUpdateSuggestions = useCallback(async () => {
    await dispatch(getCreationClipSuggestions(creationRequestId));
    return suggestedClipsSelector(store.getState());
  }, [creationRequestId, dispatch, store]);

  const handleUpdateSuggestion = useCallback(
    async (id: number) => {
      await dispatch(getCreationClipSuggestion(creationRequestId, id));
      return suggestionSelector(store.getState(), id);
    },
    [creationRequestId, dispatch, store],
  );

  const handleReprocessClip: OnReprocessClip = useCallback(
    async (suggestionId, opts) => {
      const { configJson, context } = opts || {};

      const clip = suggestedClips.find(c => c.id === suggestionId);

      if (!clip) {
        return undefined;
      }

      await dispatch(
        reprocessCreationSuggestion(
          creationRequestId,
          suggestionId,
          clip.startMillis,
          clip.endMillis,
          configJson,
          context === 'preview',
        ),
      );

      return null;
    },
    [creationRequestId, dispatch, suggestedClips],
  );

  const handleOpenInAdvancedEditor: OnOpenInAdvancedEditor = useCallback(
    async ({ id, projectUuid }): Promise<string | undefined> => {
      try {
        await dispatch(enableCreationClipSuggestion(creationRequestId, id));
        return createPath(getProjectPath(projectUuid));
      } catch {
        dispatch(showError('Error while opening the clip', 5));
        return undefined;
      }
    },
    [creationRequestId, dispatch],
  );

  const handleDownloadAll = useCallback(() => {
    dispatch(onClickDownloadAllClips({ creationRequestId }));
  }, [creationRequestId, dispatch]);

  const handleExportAll = useCallback(() => {
    dispatch(onClickExportAllClips({ creationRequestId }));
  }, [creationRequestId, dispatch]);

  return (
    <ClipSelectPage
      {...{
        suggestedClips,
        eddyProjectId,
        dislikedSuggestionIds,
        clipsPageInfo,
        source,
      }}
      requestId={creationRequestId}
      mediaType="video"
      onDislikeClip={handleDislikeClip}
      onReprocessClip={handleReprocessClip}
      onOpenInAdvancedEditor={handleOpenInAdvancedEditor}
      onSelectClip={handleSelectClip}
      onSubmit={handleSubmit}
      onUpdateSuggestions={handleUpdateSuggestions}
      onUpdateSuggestion={handleUpdateSuggestion}
      onDownloadAll={handleDownloadAll}
      onExportAll={handleExportAll}
    />
  );
};

export default VideoTranscriptionSuggestedClipsPage;
